import { cn } from 'cn';
import * as React from 'react';

type LinkProps = Omit<React.ComponentProps<'a'>, 'rel' | 'target'> & { href: string };

/** An absolute http(s) URL leaves the app, so it opens in a new tab. */
function isAbsolute(href: string) {
  return /^https?:\/\//i.test(href);
}

/**
 * An anchor with RIA link semantics.
 *
 * Carries no appearance beyond the focus treatment: the in-scope links share
 * no visual language, so styling belongs at the call site. The focus ring is
 * a deliberate divergence from the MUI baseline, which has none for anchors
 * (Theme/index.ts:602-613) and therefore fails AC 4.
 *
 * External `rel`/`target` are not caller-overridable — that is the reverse-tabnabbing
 * contract. tw-shadcn has no logger dependency, so the missing-name guard uses
 * console.warn behind a NODE_ENV check.
 */
function Link({ href, children, className, ...props }: LinkProps) {
  const external = isAbsolute(href);

  if (process.env.NODE_ENV !== 'production') {
    const named =
      Boolean(props['aria-label']) ||
      Boolean(props['aria-labelledby']) ||
      (typeof children === 'string' ? children.trim().length > 0 : children != null);
    if (!named) {
      console.warn(
        `Link to "${href}" renders without an accessible name. ` +
          'Give it text content or an aria-label.',
      );
    }
  }

  return (
    <a
      data-slot="link"
      href={href}
      {...props}
      className={cn('outline-none focus-visible:focus-ring', className)}
      {...(external ? { rel: 'noopener noreferrer', target: '_blank' } : {})}
    >
      {children}
    </a>
  );
}

export { Link };
